/**
 * Viability bands — two lines, three bands (Planning Resource Model PRD, Part B).
 *
 * Harry's framing from the 2026-08-24 walkthrough: the binary line should
 * probably be two lines — "below the line for sure, above the line maybe, and
 * above the line for sure". Today `isAboveLine` collapses Fully Achievable and
 * Partially Achievable into one side of one line, and confidence is nowhere.
 *
 * NO new arithmetic. The same pure engine runs twice and the band is a
 * read-model computed from the pair:
 *
 *   Optimistic   raw estimates (×1.0)                     unbuffered pools
 *   Conservative estimates × work-type uncertainty mult.  pools × (1 − buffer)
 *
 * The conservative pass is today's run: the capacity buffer is already applied
 * upstream in `deriveCapacityFields`, and the uncertainty multiplier is 1.0
 * until work-type recipes reach the engine. So at the default multiplier this
 * costs exactly one extra pass, and `runPlanningEngine` output is untouched.
 *
 * NO Supabase imports. Pure deterministic functions only, except for the
 * `generatedAt` stamp.
 */

import { runPlanningEngine } from './planningEngineModule';
import type {
  CapacityModelRow,
  PlanningEngineInput,
  PlanningEngineOutput,
  Priority,
  PriorityCapacityRow,
  PrioritySummary,
  ViabilityBand,
  ViabilityBandReadModel,
  ViabilityBandRecord,
} from './planningEngineModule.types';

/** Default until work-type uncertainty multipliers reach the engine. */
export const DEFAULT_CONSERVATIVE_DEMAND_MULTIPLIER = 1;

// ---------------------------------------------------------------------------
// §1 — Building the two passes
// ---------------------------------------------------------------------------

/**
 * Rebuilds a capacity row's derived fields with the scenario buffer removed.
 *
 * Derived from `weeklyCapacityHours` rather than by dividing the buffered
 * value by (1 − buffer): a 100% buffer would divide by zero, and a buffer
 * above 100% would flip the sign.
 */
function unbufferedCapacity<T extends CapacityModelRow | PriorityCapacityRow>(
  row: T,
  planningWeeks: number,
  defaultWorkUnitHours: number,
): T {
  const effectiveCapacityHours = row.weeklyCapacityHours * planningWeeks;
  return {
    ...row,
    effectiveCapacityHours,
    workUnitCapacity:
      defaultWorkUnitHours > 0 ? effectiveCapacityHours / defaultWorkUnitHours : 0,
  };
}

/** The optimistic pass: same demand, pools with the capacity buffer taken off. */
export function buildOptimisticInput(input: PlanningEngineInput): PlanningEngineInput {
  const { scenario } = input;
  if (scenario.capacityBufferPercent === 0) return input;

  const { planningWeeks, defaultWorkUnitHours } = scenario;
  return {
    ...input,
    scenario: { ...scenario, capacityBufferPercent: 0 },
    capacityRows: input.capacityRows.map((r) =>
      unbufferedCapacity(r, planningWeeks, defaultWorkUnitHours),
    ),
    priorityCapacityRows: input.priorityCapacityRows.map((r) =>
      unbufferedCapacity(r, planningWeeks, defaultWorkUnitHours),
    ),
  };
}

/**
 * The conservative pass: same pools (already buffered), demand inflated by the
 * work-type uncertainty multiplier.
 *
 * At multiplier 1 this returns the input untouched, which is why the caller can
 * reuse the primary run rather than executing a third pass.
 */
export function buildConservativeInput(
  input: PlanningEngineInput,
  demandMultiplier: number,
): PlanningEngineInput {
  if (demandMultiplier === 1) return input;
  const m = demandMultiplier;
  return {
    ...input,
    workUnits: input.workUnits.map((wu) => ({
      ...wu,
      effortEstimateHours: wu.effortEstimateHours * m,
      countedEffortHours: wu.countedEffortHours == null ? null : wu.countedEffortHours * m,
    })),
    priorities: input.priorities.map((p) => ({
      ...p,
      priorityLevelWuEstimate:
        p.priorityLevelWuEstimate == null ? null : p.priorityLevelWuEstimate * m,
      remainingWuEstimate: p.remainingWuEstimate == null ? null : p.remainingWuEstimate * m,
    })),
  };
}

// ---------------------------------------------------------------------------
// §2 — Rank distance (Wes's spatial-distance idea)
// ---------------------------------------------------------------------------

/**
 * The gap between #1 and #2 is not the gap between #2 and #3. This measures
 * that gap from the scores the ranked list already carries, so the band view
 * can encode *distance* rather than only order.
 *
 * Returns 0 for every item when the set carries no usable score — which renders
 * as the uniform spacing the view has today.
 */
export function computeRankSeparation(priorities: Priority[]): {
  separationByPriorityId: Map<string, number>;
  basis: ViabilityBandReadModel['rankDistanceBasis'];
} {
  const separationByPriorityId = new Map<string, number>();
  for (const p of priorities) separationByPriorityId.set(p.id, 0);

  const bases: Array<{
    key: ViabilityBandReadModel['rankDistanceBasis'];
    read: (p: Priority) => number | null;
  }> = [
    { key: 'valueScore', read: (p) => p.valueScore },
    { key: 'costOfDelay', read: (p) => p.costOfDelay },
    { key: 'impactScore', read: (p) => p.impactScore },
  ];

  const ranked = [...priorities].sort((a, b) => {
    const ra = a.forceRank ?? Number.POSITIVE_INFINITY;
    const rb = b.forceRank ?? Number.POSITIVE_INFINITY;
    if (ra !== rb) return ra - rb;
    return a.createdAt.localeCompare(b.createdAt);
  });

  for (const basis of bases) {
    // A basis is usable only when every ranked item carries it — a partial
    // column would read as "huge gap" wherever the score is simply missing.
    const scores = ranked.map(basis.read);
    if (scores.length < 2 || scores.some((s) => s == null || !Number.isFinite(s))) continue;

    const deltas: number[] = [];
    for (let i = 1; i < ranked.length; i += 1) {
      deltas.push(Math.abs((scores[i - 1] as number) - (scores[i] as number)));
    }
    const maxDelta = Math.max(...deltas);
    if (maxDelta <= 0) continue; // every score identical — order carries no distance

    for (let i = 1; i < ranked.length; i += 1) {
      separationByPriorityId.set(ranked[i].id, deltas[i - 1] / maxDelta);
    }
    return { separationByPriorityId, basis: basis.key };
  }

  return { separationByPriorityId, basis: 'none' };
}

// ---------------------------------------------------------------------------
// §3 — Band assignment
// ---------------------------------------------------------------------------

/** Today's binary predicate, kept as the boundary the bands subdivide. */
export function isAboveLine(summary: PrioritySummary): boolean {
  return (
    summary.feasibility === 'Fully Achievable' ||
    summary.feasibility === 'Partially Achievable'
  );
}

/**
 * Assigns one priority's band from its conservative and optimistic summaries.
 *
 * - **firm**   — carries fully in the conservative pass, with no participation risk.
 * - **at risk** — survives only the optimistic pass, or carries only partially,
 *   or carries fully but its cross-functional participation is not confirmed.
 * - **out**    — fails both passes.
 */
export function assignBand(
  conservative: PrioritySummary,
  optimistic: PrioritySummary,
  capacityBufferPercent: number,
): { band: ViabilityBand; bandReason: string } {
  const conservativeIn = isAboveLine(conservative);
  const optimisticIn = isAboveLine(optimistic);

  if (!conservativeIn && !optimisticIn) {
    return {
      band: 'out',
      bandReason:
        conservative.feasibility === 'No Work'
          ? 'No plannable work defined — size it before it can be planned.'
          : 'Does not fit under either the optimistic or the conservative pass.',
    };
  }

  if (!conservativeIn) {
    const buffer = Math.round(capacityBufferPercent * 100);
    return {
      band: 'at_risk',
      bandReason:
        `Fits only against unbuffered capacity — the ${buffer}% capacity buffer ` +
        'is enough to push it out.',
    };
  }

  if (conservative.feasibility === 'Partially Achievable') {
    return {
      band: 'at_risk',
      bandReason:
        `Only ${conservative.coveragePercent}% of its work fits ` +
        `(${conservative.includedWU.toFixed(1)} of ${conservative.requiredWU.toFixed(1)} WU).`,
    };
  }

  if (conservative.hasParticipationRisk) {
    return {
      band: 'at_risk',
      bandReason: `Capacity is there, but participation is not settled — ${conservative.decisionWithParticipation}.`,
    };
  }

  return { band: 'firm', bandReason: '' };
}

// ---------------------------------------------------------------------------
// §4 — The read model
// ---------------------------------------------------------------------------

/**
 * The engine's own reason per priority, lifted off the plan records.
 *
 * The specific answers — "requires payments_domain at level 6 or above, three
 * people in engineering hold it below that level" — are written on the record
 * by the qualified-capacity ledger. Without this they never reach the view
 * anybody actually presents from, which is the whole point of the exercise.
 *
 * Priority-grain wins when present (it IS the decision in Priority Estimate
 * mode); otherwise the first excluded work unit's reason stands for the
 * priority, since that is the first thing that stopped it.
 */
function collectExclusionReasons(output: PlanningEngineOutput): Map<string, string> {
  const byPriority = new Map<string, string>();

  for (const record of output.planRecords) {
    const priorityId = record.priority?.id;
    if (!priorityId || record.planDecision === 'Included') continue;
    if (!byPriority.has(priorityId)) byPriority.set(priorityId, record.decisionReason);
  }

  // Set last so it overwrites any work-unit reason for the same priority.
  for (const record of output.priorityPlanRecords) {
    if (record.planDecision === 'Included') continue;
    byPriority.set(record.priority.id, record.decisionReason);
  }

  return byPriority;
}

export function computeViabilityBands(args: {
  conservative: PlanningEngineOutput;
  optimistic: PlanningEngineOutput;
  conservativeDemandMultiplier: number;
  capacityBufferPercent: number;
}): ViabilityBandReadModel {
  const { conservative, optimistic, conservativeDemandMultiplier, capacityBufferPercent } = args;

  const optimisticById = new Map(
    optimistic.prioritySummaries.map((s) => [s.priority.id, s]),
  );
  const reasonByPriorityId = collectExclusionReasons(conservative);
  const { separationByPriorityId, basis } = computeRankSeparation(
    conservative.prioritySummaries.map((s) => s.priority),
  );

  const records: ViabilityBandRecord[] = conservative.prioritySummaries.map((cons) => {
    // A priority missing from the optimistic pass would mean the two passes saw
    // different sets, which cannot happen: only capacity is varied. Falling back
    // to the conservative summary keeps the read model total rather than
    // silently dropping a row if that ever changes.
    const opt = optimisticById.get(cons.priority.id) ?? cons;
    const { band, bandReason } = assignBand(cons, opt, capacityBufferPercent);
    return {
      priorityId: cons.priority.id,
      band,
      bandReason,
      conservativeFeasibility: cons.feasibility,
      optimisticFeasibility: opt.feasibility,
      conservativeCoveragePercent: cons.coveragePercent,
      optimisticCoveragePercent: opt.coveragePercent,
      requiredWU: cons.requiredWU,
      exclusionReason: band === 'firm' ? null : (reasonByPriorityId.get(cons.priority.id) ?? null),
      rankSeparation: separationByPriorityId.get(cons.priority.id) ?? 0,
    };
  });

  return {
    records,
    firmCount: records.filter((r) => r.band === 'firm').length,
    atRiskCount: records.filter((r) => r.band === 'at_risk').length,
    outCount: records.filter((r) => r.band === 'out').length,
    conservativeDemandMultiplier,
    capacityBufferPercent,
    rankDistanceBasis: basis,
    generatedAt: new Date().toISOString(),
  };
}

// ---------------------------------------------------------------------------
// §5 — Entry point
// ---------------------------------------------------------------------------

/**
 * Runs the engine and attaches the band read model.
 *
 * The returned `PlanningEngineOutput` is always the **primary** pass —
 * byte-identical to `runPlanningEngine(input)` — with `viabilityBands` added.
 * The optimistic and conservative passes exist only to produce the bands, so
 * raising the uncertainty multiplier later sharpens the middle band without
 * moving the numbers every existing view already shows.
 *
 * Cost: one extra pass while the multiplier is 1.0 and the scenario carries a
 * capacity buffer; two extra once uncertainty multipliers ship; none at all for
 * an unbuffered scenario at multiplier 1.0, where all three passes coincide.
 */
export function runPlanningEngineWithBands(
  input: PlanningEngineInput,
  options: { conservativeDemandMultiplier?: number } = {},
): PlanningEngineOutput {
  const conservativeDemandMultiplier =
    options.conservativeDemandMultiplier ?? DEFAULT_CONSERVATIVE_DEMAND_MULTIPLIER;

  // The primary run. Buffered pools, raw estimates — exactly what ships today.
  const primary = runPlanningEngine(input);

  // Conservative: buffered pools + uncertainty-inflated demand. Identical to the
  // primary run until work-type multipliers reach the engine.
  const conservativeInput = buildConservativeInput(input, conservativeDemandMultiplier);
  const conservative =
    conservativeInput === input ? primary : runPlanningEngine(conservativeInput);

  // Optimistic: raw estimates against unbuffered pools.
  const optimisticInput = buildOptimisticInput(input);
  const optimistic = optimisticInput === input ? primary : runPlanningEngine(optimisticInput);

  return {
    ...primary,
    viabilityBands: computeViabilityBands({
      conservative,
      optimistic,
      conservativeDemandMultiplier,
      capacityBufferPercent: input.scenario.capacityBufferPercent,
    }),
  };
}
